import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { useLinkClickHandler } from 'react-router-dom';
import { FacilityType, YardLayoutType } from '../../types/Facility';
import { Modal } from '@mui/material';
import { Close } from '@mui/icons-material';
import YardManagement from '../YardManagement/YardManagement';

import { getAuthToken } from '../../utils/cookieUtils';
// Environment variables
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

const Facilities = () => {
  const [facilities, setFacilities] = useState<FacilityType[]>([]);
  const [searchedFacilities, setSearchedFacilities] = useState<FacilityType[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [addFacilitySuccess, setAddFacilitySuccess] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [yardLayouts, setYardLayouts] = useState<YardLayoutType[]>([]);
  const [selectedYardLayoutId, setSelectedYardLayoutId] = useState<string | null>(null);
  const [loadingYardLayout, setLoadingYardLayout] = useState(false);
  const [showYardLayoutModal, setShowYardLayoutModal] = useState(false);

  useEffect(() => {
    const fetchFacilities = async () => {
      setLoading(true);
      try {
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
      } catch (error) {
        setError('An error occurred while fetching facilities');
        console.error(error);
      }
      setLoading(false);
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    setSearchedFacilities(
      facilities.filter((facility) =>
        facility.name.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, facilities]);

  // Fetch yard layout when a facility is selected
  useEffect(() => {
    if (!selectedFacilityId) return;
    
    const fetchYardLayouts = async () => {
      setLoadingYardLayout(true);
      try {
        // GraphQL query for yard layouts with expanded structure
        const yardLayoutQuery = `
          query {
            yard_layouts(facility_id: "${selectedFacilityId}") {
              uuid
              name
              description
              facility_id
              depth
              parent_zone_uuid
              configuration {
                color
                orientation
                width
                height
              }
              yard_spots {
                uuid
                name
                facility_id
                facility_zone_uuid
                order_number
                orientation
                configuration {
                  width
                  height
                  capacity
                }
              }
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch yard layout using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: yardLayoutQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Set yard layouts from the response
        setYardLayouts(result.data.yard_layouts);
        
        // Select the first layout by default
        if (result.data.yard_layouts.length > 0) {
          setSelectedYardLayoutId(result.data.yard_layouts[0].uuid);
        }
        
        setShowYardLayoutModal(true);
      } catch (error) {
        setError('An error occurred while fetching yard layouts');
        console.error(error);
      }
      setLoadingYardLayout(false);
    };
    
    fetchYardLayouts();
  }, [selectedFacilityId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleAddShipmentModalClose = () => {
    setShowAddFacilityModal(false);
    setAddFacilitySuccess(false);
  };

  const handleYardLayoutModalClose = () => {
    setShowYardLayoutModal(false);
    setSelectedFacilityId(null);
    setYardLayouts([]);
    setSelectedYardLayoutId(null);
  };

  const handleViewYardLayout = (facilityId: string) => {
    setSelectedFacilityId(facilityId);
  };

  const handleYardLayoutChange = (layoutId: string) => {
    setSelectedYardLayoutId(layoutId);
  };

  return (
    <div className="p-5 w-full">
      <div className="bg-[#FBFBFB] p-3 shadow-[1px_0_10px_1px_rgba(0,0,0,0.3)] text-sm">
        <div className="flex justify-between mb-2">
          <div className="w-full">
            <div className="flex gap-2 items-center mt-2">
              Filter By:
              <input
                type="search"
                className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                onChange={handleSearchChange}
                value={search}
                placeholder="Search"
              />
              <button
                className={twMerge(
                  'bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded ml-auto',
                  loading && 'bg-gray-300 hover:bg-gray-300'
                )}
                onClick={() => setShowAddFacilityModal(true)}
              >
                Add Facility
              </button>
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </div>
        </div>

        <table className="table-auto border-slate-300 border-[1px] min-w-[66%]">
          <thead className="border-red-400 border-b-[3px]">
            <tr>
              <th className="p-2">Facility Name</th>
              <th className="p-2">ID</th>
              <th className="p-2">Address</th>
              <th className="p-2"># of Gates</th>
            </tr>
          </thead>
          {!loading && (
            <tbody className="text-center">
              {searchedFacilities.map((facility: FacilityType) => (
                <Row 
                  key={facility.id} 
                  facility={facility} 
                  onViewYardLayout={handleViewYardLayout}
                />
              ))}
            </tbody>
          )}
        </table>

        {loading && (
          <div className="flex flex-col items-center mt-4">
            <CircularProgress />
            <p className="mt-2">Loading...</p>
          </div>
        )}
      </div>
      
      {/* Add Facility Modal */}
      <Modal
        open={showAddFacilityModal}
        onClose={handleAddShipmentModalClose}
        className="flex justify-center items-center"
      >
        <div className="w-96 bg-white border p-5 min-h-48 relative flex flex-col">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={handleAddShipmentModalClose}
          />
          <h1 className="text-xl mb-5">Add Facility</h1>

          {addFacilitySuccess ? (
            <div className="bg-green-100 p-2 border-slate-300 border-[1px] text-sm mb-3">
              Facility Added!
            </div>
          ) : (
            <>
              <label className="mt-3 flex flex-col">
                Name
                <input
                  type="text"
                  placeholder="Name"
                  className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                />
              </label>
              <label className="mt-3 flex flex-col">
                IDs
                <input
                  type="text"
                  placeholder="IDs"
                  className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                />
              </label>
              <label className="mt-3 flex flex-col">
                Address
                <input
                  type="text"
                  placeholder="Address"
                  className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                />
              </label>
              <label className="mt-3 flex flex-col">
                # Gates
                <input
                  type="text"
                  placeholder="# Gates"
                  className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                />
              </label>
              <label className="mt-3 flex flex-col">
                Open Gate Hours
                <input
                  type="text"
                  placeholder="Open Gate Hours"
                  className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                />
              </label>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded ml-auto mt-3"
                onClick={() => {
                  setAddFacilitySuccess(true);
                  setTimeout(() => {
                    handleAddShipmentModalClose();
                  }, 3000);
                }}
              >
                Add Facility
              </button>
            </>
          )}
        </div>
      </Modal>

      {/* Yard Layout Modal - Updated to use YardManagement component */}
      <Modal
        open={showYardLayoutModal}
        onClose={handleYardLayoutModalClose}
        className="flex justify-center items-center"
      >
        <div className="w-[90%] max-w-[1200px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative flex flex-col">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={handleYardLayoutModalClose}
          />
          <h1 className="text-xl mb-5">Yard Management</h1>

          {loadingYardLayout ? (
            <div className="flex flex-col items-center mt-4">
              <CircularProgress />
              <p className="mt-2">Loading yard layout...</p>
            </div>
          ) : yardLayouts.length > 0 ? (
            <div>
              {/* Dropdown to select between different yard layouts */}
              {yardLayouts.length > 1 && (
                <div className="mb-4">
                  <label className="font-medium">Select Yard Layout:</label>
                  <select 
                    value={selectedYardLayoutId || ''}
                    onChange={(e) => handleYardLayoutChange(e.target.value)}
                    className="ml-2 border p-1 rounded"
                  >
                    {yardLayouts.map(layout => (
                      <option key={layout.uuid} value={layout.uuid}>
                        {layout.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Pass yard layout data to YardManagement component */}
              <YardManagement 
                yardLayouts={yardLayouts}
                selectedLayoutId={selectedYardLayoutId}
                onLayoutChange={handleYardLayoutChange}
              />
            </div>
          ) : (
            <p className="text-gray-500">No yard layout information available</p>
          )}
        </div>
      </Modal>
    </div>
  );
};

interface RowProps {
  facility: FacilityType;
  onViewYardLayout: (facilityId: string) => void;
}

const Row = ({ facility, onViewYardLayout }: RowProps) => {
  const { id, name, address, gate_count } = facility;
  const handleClick = useLinkClickHandler(`/facilities/${id}`);

  const handleViewYardLayout = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click handler from firing
    onViewYardLayout(id);
  };

  return (
    <tr
      onClick={(e: any) => handleClick(e)}
      className="hover:bg-[#f5f5f5] border-slate-200 border-b-[1px] cursor-pointer odd:bg-[#f9f9f9] last:border-b-0"
    >
      <td className="p-2">{name}</td>
      <td className="p-2">{id}</td>
      <td className="p-2">{address}</td>
      <td className="p-2">{gate_count}</td>
     
    </tr>
  );
};

export default Facilities;
